import React, { useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import Tag from "../../../../components/tag/tag";
import {
    HeroSection,
    HeroContent,
    HeroTitle,
    HeroSubtitle,
    HeroButtonWrapper,
    SectionTitle,
    FeatureSection,
    FeatureCard,
    FeatureIcon,
    SectionSubtitle,
    SectionBadge,
    StatsSection,
    StatsTitle,
    StatsSubtitle,
    FeatureCardsWrapper,
    FeatureList,
    FeatureListItem,
    FeatureListItemLabel,
    FeatureDescription,
    FeatureTitle,
    ManagdInNumbersCard,
    HowItWorksSection,
    HowItWorksStepsWrapper,
    HowItWorksDesktopLine,
    HowItWorksMobileLine,
    HowItWorksStep,
    HowItWorksIconWrapper,
    HowItWorksStepBox,
    HowItWorksStepTitle,
    HowItWorksStepDescription,
    ExploreSection,
    CTAButtonWrapper,
    FindNewHomeSection,
    UnitCard,
    UnitCardsWrapper,
    UnitCardImageWrapper,
    UnitCardImage,
    UnitCardTag,
    UnitCardTitle,
    UnitLocation,
    UnitStatistics,
    UnitStatisticItem,
    UnitStatisticItemLabel,
    UnitCardContent,
    UnitLocationWrapper,
    UnitCardsIndicators,
    UnitCardsIndicatorDot,
    HeroBadge,
} from "./tenant-home-page.styles";
import HouseKeysIcon from "../../../../assets/images/house_keys.webp";
import SupportIcon from "../../../../assets/images/support_icon.webp";
import TogetherWavingIcon from "../../../../assets/images/together_waving.webp";
import { CSSTransition } from "react-transition-group";
import useIsMobile from "../../../../hooks/useIsMobile";
import TabBar from "../../components/tab-bar/tab-bar";
import CTAButton from "../../../../components/cta-button/cta-button";
import MaterialIcon from "../../../../components/material-icon";
import { colorPalette } from "../../../../utils/theme";

// Screenshots
import DashboardScreen from "../../../../assets/images/landing-page/dashboard-screen.webp";
import UnitScreen from "../../../../assets/images/landing-page/unit-screen.webp";
import FinanceScreen from "../../../../assets/images/landing-page/finance-screen.webp";
import ContractsScreen from "../../../../assets/images/landing-page/contracts-screen.webp";

import useCustomNavigate from "../../../../hooks/useCustomNavigate";
import Footer from "../../components/footer/footer";
import { useTranslation } from "react-i18next";
import ControlAnywhere from "./components/control-anywhere/control-anywhere";
import FAQSection from "../../components/faq-section/faq-section";
import JsonLd from "../../../../components/json-ld/json-ld";
import TheVybeImage from "../../../../assets/images/landing-page/homes/the-vybe.webp";
import TheIvyImage from "../../../../assets/images/landing-page/homes/the-ivy.webp";
import HavenResidencesImage from "../../../../assets/images/landing-page/homes/haven-residences.webp";
import { useContentArea } from "../../../../context/ContentAreaContext";
import { useLocation } from "react-router-dom";
import { TFunction } from "i18next";
import { useMemo } from "react";

const getServiceItems = (t: TFunction) => [
    {
        icon: HouseKeysIcon,
        width: 120,
        height: 90,
        iconAlt: "House Keys Icon",
        title: t('tenant_home.services.direct_rental.title'),
        description: t('tenant_home.services.direct_rental.description'),
    },
    {
        icon: SupportIcon,
        width: 80,
        height: 90,
        iconAlt: "Support Icon",
        title: t('tenant_home.services.on_site_support.title'),
        description: t('tenant_home.services.on_site_support.description'),
    },
    {
        icon: TogetherWavingIcon,
        width: 120,
        height: 90,
        iconAlt: "Together Waving Icon",
        title: t('tenant_home.services.long_term_living.title'),
        description: t('tenant_home.services.long_term_living.description'),
    }
];

const APP_ITEMS = [
    {
        id: 1,
        title: "Portfolio-Tracking in Echtzeit",
        description: "Tracke die Performance deiner Immobilien in einer Übersicht – jederzeit und überall.",
        image: DashboardScreen,
    },
    {
        id: 2,
        title: "Verfolge den Status deiner Einheiten",
        description: "Behalte den Status deiner Immobilien im Blick - Während der Bauphase und in der Vermietung.",
        image: UnitScreen,
    },
    {
        id: 3,
        title: "Finanzielle Transparenz & Unterlagen",
        description: "Greife jederzeit auf wichtige Abrechnungen, Einnahmen & Ausgaben oder Transaktionen zu.",
        image: FinanceScreen,
    },
    {
        id: 4,
        title: "Alle Verträge an einem Ort",
        description: "Bewahre alle vertragsrelevanten Unterlagen zu deiner Immobilie und den Mietverhältnissen an einem Ort auf.",
        image: ContractsScreen,
    },
];

const getHowItWorksSteps = (t: TFunction) => [
    {
        icon: "contract_edit" as const,
        title: t('tenant_home.how_it_works.steps.reserve_apartment.title'),
        description: t('tenant_home.how_it_works.steps.reserve_apartment.description'),
    },
    {
        icon: "file_export" as const,
        title: t('tenant_home.how_it_works.steps.upload_documents.title'),
        description: t('tenant_home.how_it_works.steps.upload_documents.description'),
    },
    {
        icon: "signature" as const,
        title: t('tenant_home.how_it_works.steps.digital_signing.title'),
        description: t('tenant_home.how_it_works.steps.digital_signing.description'),
    },
    {
        icon: "real_estate_agent" as const,
        title: t('tenant_home.how_it_works.steps.ongoing_support.title'),
        description: t('tenant_home.how_it_works.steps.ongoing_support.description'),
    },
];

const UNIT_CARDS = [
    {
        image: TheVybeImage,
        title: "The Vybe",
        location: "Jumeirah Village Circle",
        bedrooms: "Studio, 1 BR, 2 BR",
        size: "550-1300 Sqft",
        price: "FROM AED 85.000",
        tag: "Available"
    },
    {
        image: TheIvyImage,
        title: "The Ivy",
        location: "Jumeirah Village Triangle",
        bedrooms: "Studio, 1 BR, 2 BR",
        size: "550-1300 Sqft",
        price: "FROM AED 75.000",
        tag: "Available"
    },
    {
        image: HavenResidencesImage,
        title: "Haven Residences",
        location: "Jumeirah Village Circle",
        bedrooms: "Studio, 1 BR, 2 BR",
        size: "550-1300 Sqft",
        price: "FROM AED 60.000",
        tag: "Available"
    }
];

const TenantHomePage: React.FC = () => {
    const isMobile = useIsMobile();
    const navigate = useCustomNavigate();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { contentAreaRef } = useContentArea();

    // Memoize dynamic content to update when language changes
    const serviceItems = useMemo(() => getServiceItems(t), [t]);
    const howItWorksSteps = useMemo(() => getHowItWorksSteps(t), [t]);

    // References
    const featuresRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<HTMLDivElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);

    // States
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAppSceneTimerDeactived, setIsAppSceneTimerDeactived] = useState(false);
    const [featuresVisible, setFeaturesVisible] = useState(false);
    const [appVisible, setAppVisible] = useState(false);
    const [statsVisible, setStatsVisible] = useState(false);
    const [isHeroVisible, setIsHeroVisible] = useState(false);
    const [activeUnitIndex, setActiveUnitIndex] = useState(0);
    const unitCardsWrapperRef = useRef<HTMLDivElement>(null);

    // Effects
    useEffect(() => {
        const anchor = location.hash.replace("#", "");

        if (anchor) {
            const scrollToElement = () => {
                if (contentAreaRef.current) {

                    const targetElement = contentAreaRef.current.querySelector(`#${anchor}`);

                    if (targetElement) {
                        const offset = 65; // Adjust for the fixed header/navbar height
                        const contentAreaTop = contentAreaRef.current.getBoundingClientRect().top;
                        const elementPosition = targetElement.getBoundingClientRect().top - contentAreaTop;
                        const offsetPosition = elementPosition - offset;

                        // Smooth scroll within the content area
                        contentAreaRef.current.scrollTo({
                            top: contentAreaRef.current.scrollTop + offsetPosition,
                            behavior: "smooth",
                        });
                    }
                }
            };

            // Wait for DOM updates before scrolling
            setTimeout(scrollToElement, 200);
        }
    }, [contentAreaRef, location]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsHeroVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isAppSceneTimerDeactived) {
            const interval = setInterval(() => {
                setActiveIndex((prevIndex) => (prevIndex + 1) % APP_ITEMS.length);
            }, 4000); // Trigger every 4 seconds

            return () => clearInterval(interval);
        }
    }, [isAppSceneTimerDeactived]);

    useEffect(() => {
        const currentStatsRef = statsRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setStatsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentStatsRef) {
            observer.observe(currentStatsRef);
        }

        return () => {
            if (currentStatsRef) observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const currentAppRef = appRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setAppVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentAppRef) {
            observer.observe(currentAppRef);
        }

        return () => {
            if (currentAppRef) observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const currentFeaturesRef = featuresRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setFeaturesVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.2 } // Trigger when 20% of the element is visible
        );

        if (currentFeaturesRef) {
            observer.observe(currentFeaturesRef);
        }

        return () => {
            if (currentFeaturesRef) observer.disconnect();
        };
    }, []);

    // Handle unit cards scroll tracking
    const handleUnitCardsScroll = () => {
        if (unitCardsWrapperRef.current) {
            const container = unitCardsWrapperRef.current;
            const scrollLeft = container.scrollLeft;
            const cardWidth = 380 + 16; // card width + gap
            const paddingLeft = 32; // Container's left padding

            // Calculate which card's start position is closest to current scroll
            const adjustedScrollLeft = scrollLeft + paddingLeft;
            const newIndex = Math.round(adjustedScrollLeft / cardWidth);
            setActiveUnitIndex(Math.max(0, Math.min(newIndex, UNIT_CARDS.length - 1)));
        }
    };

    const handleDotClick = (index: number) => {
        if (unitCardsWrapperRef.current) {
            const container = unitCardsWrapperRef.current;
            const cardWidth = 380 + 16; // card width + gap
            const paddingLeft = 32; // Container's left padding

            // Calculate scroll position - each card should align to start with proper spacing
            const scrollLeft = (index * cardWidth) - paddingLeft;

            container.scrollTo({
                left: Math.max(0, scrollLeft), // Ensure we don't scroll to negative values
                behavior: 'smooth'
            });
        }
    };

    const onClickContactRentalConcierge = () => {
        window.open('https://wa.me/+971527253219', '_blank');
    }

    useEffect(() => {
        const wrapper = unitCardsWrapperRef.current;
        if (wrapper) {
            wrapper.addEventListener('scroll', handleUnitCardsScroll);
            return () => wrapper.removeEventListener('scroll', handleUnitCardsScroll);
        }
    }, []);

    return (
        <>
            <Row>
                <Col xs={12} md={11} xxl={10} className="mx-auto">
                    <TabBar />

                    {/* Hero Section */}
                    <HeroSection>
                        <CSSTransition in={isHeroVisible} timeout={800} classNames="slide" unmountOnExit>
                            <>
                                <Row className="align-items-center" style={isMobile ? undefined : { paddingLeft: 50, paddingRight: 50 }}>
                                    <Col xs={12} md={7} className="mx-auto">
                                        <HeroContent>
                                            <HeroBadge>{t('tenant_home.hero.badge')}</HeroBadge>
                                            <HeroTitle>
                                                {t('tenant_home.hero.title').split('\n').map((line, index) => (
                                                    <React.Fragment key={index}>
                                                        {line}
                                                        {index === 0 && <br />}
                                                    </React.Fragment>
                                                ))}
                                            </HeroTitle>
                                            <HeroSubtitle>{t('tenant_home.hero.subtitle')}</HeroSubtitle>
                                            {!isMobile && (
                                                <HeroButtonWrapper>
                                                    <CTAButton
                                                        variant="primary"
                                                        size="L"
                                                        label={t('buttons.show_available_units')}
                                                        onClick={() => navigate("/tenant-home#available_units")}
                                                        fullWidth
                                                    />
                                                    <CTAButton
                                                        variant="secondary"
                                                        size="L"
                                                        label={t('buttons.how_it_works')}
                                                        onClick={() => navigate("/tenant-home#how_it_works")}
                                                        fullWidth
                                                    />
                                                </HeroButtonWrapper>
                                            )}
                                        </HeroContent>
                                    </Col>
                                </Row>
                            </>
                        </CSSTransition>
                    </HeroSection>

                    {/* Control Anywhere Section */}
                    <ControlAnywhere />

                    {/* Features Section */}
                    <FeatureSection ref={featuresRef}>
                        <CSSTransition in={featuresVisible} timeout={800} classNames="slide" unmountOnExit>
                            <div>
                                <SectionBadge>{t('tenant_home.services.badge')}</SectionBadge>
                                <SectionTitle>
                                    {t('tenant_home.services.section_title')}
                                </SectionTitle>
                                <SectionSubtitle>
                                    {t('tenant_home.services.section_subtitle')}
                                </SectionSubtitle>

                                {isMobile ? (
                                    <FeatureCardsWrapper>
                                        {serviceItems.map((item, index) => (
                                            <FeatureCard key={index}>
                                                <FeatureIcon src={item.icon} alt={item.iconAlt} width={item.width} height={item.height} draggable={false} />
                                                <FeatureTitle>{item.title}</FeatureTitle>
                                                <FeatureDescription>{item.description}</FeatureDescription>
                                            </FeatureCard>
                                        ))}
                                    </FeatureCardsWrapper>
                                ) : (
                                    <Row className="gy-3 gx-3" style={{ marginBottom: 32 }}>
                                        {serviceItems.map((item, index) => (
                                            <Col md={4} key={index}>
                                                <FeatureCard>
                                                    <FeatureIcon src={item.icon} alt={item.iconAlt} width={item.width} height={item.height} />
                                                    <FeatureTitle>{item.title}</FeatureTitle>
                                                    <FeatureDescription>{item.description}</FeatureDescription>
                                                </FeatureCard>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </div>
                        </CSSTransition>
                    </FeatureSection>

                    {/* Find New Home Section */}
                    <FindNewHomeSection id="available_units">
                        <div style={{ textAlign: 'center' }}>
                            <SectionBadge>{t('tenant_home.available_units.badge')}</SectionBadge>
                            <SectionTitle>
                                {t('tenant_home.available_units.title')}
                            </SectionTitle>
                            <SectionSubtitle>
                                {t('tenant_home.available_units.subtitle')}
                            </SectionSubtitle>

                            {isMobile ? (
                                <>
                                    <UnitCardsWrapper ref={unitCardsWrapperRef}>
                                        {UNIT_CARDS.map((unit, index) => (
                                            <UnitCard key={index}>
                                                <UnitCardImageWrapper>
                                                    <UnitCardImage src={unit.image} alt="Unit Image" />
                                                    {index === 0 && (
                                                        <UnitCardTag>
                                                            <Tag
                                                                type="unitStatus"
                                                                color={colorPalette.successLight}
                                                                backgroundColor={unit.tag === "Available" ? colorPalette.successDark : colorPalette.errorDark}
                                                                variant="rented"
                                                                label="New Building"
                                                                size="S"
                                                            />
                                                        </UnitCardTag>
                                                    )}
                                                </UnitCardImageWrapper>
                                                <UnitCardContent>
                                                    <UnitCardTitle>
                                                        {unit.title}
                                                    </UnitCardTitle>
                                                    <UnitLocationWrapper>
                                                        <MaterialIcon icon="distance" size={10} color={colorPalette.textDark} />
                                                        <UnitLocation>{unit.location}</UnitLocation>
                                                    </UnitLocationWrapper>
                                                    <UnitStatistics>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="bed" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.bedrooms}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="straighten" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.size}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="shoppingmode" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.price}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                    </UnitStatistics>
                                                </UnitCardContent>
                                            </UnitCard>
                                        ))}
                                    </UnitCardsWrapper>
                                    <UnitCardsIndicators>
                                        {UNIT_CARDS.map((_, index) => (
                                            <UnitCardsIndicatorDot
                                                key={index}
                                                isActive={activeUnitIndex === index}
                                                onClick={() => handleDotClick(index)}
                                            />
                                        ))}
                                    </UnitCardsIndicators>
                                </>
                            ) : (
                                <Row className="g-3">
                                    {UNIT_CARDS.map((unit, index) => (
                                        <Col xs={12} md={4} key={index}>
                                            <UnitCard>
                                                <UnitCardImageWrapper>
                                                    <UnitCardImage src={unit.image} alt="Unit Image" />
                                                    {index === 0 && (
                                                        <UnitCardTag>
                                                            <Tag
                                                                type="unitStatus"
                                                                color={colorPalette.successLight}
                                                                backgroundColor={unit.tag === "Available" ? colorPalette.successDark : colorPalette.errorDark}
                                                                variant="rented"
                                                                label="New Building"
                                                                size="S"
                                                            />
                                                        </UnitCardTag>
                                                    )}
                                                </UnitCardImageWrapper>
                                                <UnitCardContent>
                                                    <UnitCardTitle>
                                                        {unit.title}
                                                    </UnitCardTitle>
                                                    <UnitLocationWrapper>
                                                        <MaterialIcon icon="distance" size={10} color={colorPalette.textDark} />
                                                        <UnitLocation>{unit.location}</UnitLocation>
                                                    </UnitLocationWrapper>
                                                    <UnitStatistics>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="bed" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.bedrooms}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="straighten" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.size}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                        <UnitStatisticItem>
                                                            <MaterialIcon icon="shoppingmode" size={20} color={colorPalette.blue300} />
                                                            <UnitStatisticItemLabel>{unit.price}</UnitStatisticItemLabel>
                                                        </UnitStatisticItem>
                                                    </UnitStatistics>
                                                </UnitCardContent>
                                            </UnitCard>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                            <div style={{ marginTop: isMobile ? 0 : 32 }}>
                                <SectionSubtitle>
                                    {t('tenant_home.available_units.footer_subtitle').split('\n').map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            {index === 0 && <br />}
                                        </React.Fragment>
                                    ))}
                                </SectionSubtitle>
                                <CTAButtonWrapper>
                                    <CTAButton
                                        variant="primary"
                                        size="L"
                                        icon="chat"
                                        label={t('buttons.contact_rental_concierge')}
                                        onClick={onClickContactRentalConcierge}
                                    />
                                </CTAButtonWrapper>
                            </div>
                        </div>
                    </FindNewHomeSection>

                    {/* How It Works Section */}
                    <HowItWorksSection id="how_it_works">
                        <div style={{ textAlign: 'center' }}>
                            <SectionBadge>{t('tenant_home.how_it_works.badge')}</SectionBadge>
                            <SectionTitle>
                                {t('tenant_home.how_it_works.title')}
                            </SectionTitle>
                            <SectionSubtitle>
                                {t('tenant_home.how_it_works.subtitle')}
                            </SectionSubtitle>

                            <HowItWorksStepsWrapper>
                                <HowItWorksDesktopLine />
                                <HowItWorksMobileLine />

                                {isMobile ? (
                                    <div>
                                        <HowItWorksMobileLine />
                                        {howItWorksSteps.map((step, index) => (
                                            <HowItWorksStep key={index}>
                                                <HowItWorksIconWrapper>
                                                    <MaterialIcon
                                                        icon={step.icon}
                                                        size={20}
                                                        color={colorPalette.white}
                                                    />
                                                </HowItWorksIconWrapper>
                                                <HowItWorksStepBox>
                                                    <HowItWorksStepTitle>{step.title}</HowItWorksStepTitle>
                                                    <HowItWorksStepDescription>{step.description}</HowItWorksStepDescription>
                                                </HowItWorksStepBox>
                                            </HowItWorksStep>
                                        ))}
                                    </div>
                                ) : (
                                    <Row className="gy-4 gx-2">
                                        {howItWorksSteps.map((step, index) => (
                                            <Col key={index} md={3}>
                                                <HowItWorksStep className="h-100">
                                                    <HowItWorksStepBox className="h-100">
                                                        <HowItWorksIconWrapper>
                                                            <MaterialIcon
                                                                icon={step.icon}
                                                                size={24}
                                                                color={colorPalette.white}
                                                            />
                                                        </HowItWorksIconWrapper>
                                                        <HowItWorksStepTitle>{step.title}</HowItWorksStepTitle>
                                                        <HowItWorksStepDescription>{step.description}</HowItWorksStepDescription>
                                                    </HowItWorksStepBox>
                                                </HowItWorksStep>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </HowItWorksStepsWrapper>
                        </div>
                    </HowItWorksSection>

                    {/* FAQ Section */}
                    {/*<FAQSection id="faq" />*/}

                    {/* Stats Section */}
                    <StatsSection ref={statsRef}>
                        <CSSTransition in={statsVisible} timeout={800} classNames="slide" unmountOnExit>
                            <Row className="g-3 gy-4">
                                <Col xs={12} md={7}>
                                    <ManagdInNumbersCard className="h-100" showImage>
                                        <SectionBadge>{t('tenant_home.stats.who_we_are.badge')}</SectionBadge>
                                        <StatsTitle>{t('tenant_home.stats.who_we_are.title')}</StatsTitle>
                                        <StatsSubtitle>{t('tenant_home.stats.who_we_are.subtitle')}</StatsSubtitle>
                                        <FeatureList>
                                            <FeatureListItem>
                                                <MaterialIcon icon="location_away" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.who_we_are.features.direct_rental')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="family_star" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.who_we_are.features.onsite_team')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="sound_detection_dog_barking" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.who_we_are.features.pet_friendly')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="scene" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.who_we_are.features.long_term_focus')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                        </FeatureList>
                                    </ManagdInNumbersCard>
                                </Col>
                                <Col xs={12} md={5}>
                                    <ManagdInNumbersCard className="h-100">
                                        <SectionBadge>{t('tenant_home.stats.managd_promise.badge')}</SectionBadge>
                                        <StatsTitle>{t('tenant_home.stats.managd_promise.title')}</StatsTitle>
                                        <StatsSubtitle>{t('tenant_home.stats.managd_promise.subtitle')}</StatsSubtitle>
                                        <FeatureList>
                                            <FeatureListItem>
                                                <MaterialIcon icon="signature" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.managd_promise.features.contract_creation')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="history_edu" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.managd_promise.features.digital_signing')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="admin_panel_settings" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.managd_promise.features.rera_compliance')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                            <FeatureListItem>
                                                <MaterialIcon icon="wifi_home" size={16} color={colorPalette.blue500} />
                                                <FeatureListItemLabel>{t('tenant_home.stats.managd_promise.features.move_in_support')}</FeatureListItemLabel>
                                            </FeatureListItem>
                                        </FeatureList>
                                    </ManagdInNumbersCard>
                                </Col>
                            </Row>
                        </CSSTransition>
                    </StatsSection>

                    {/* Explore Section */}
                    <ExploreSection id="explore">
                        <div style={{ textAlign: 'center' }}>
                            <SectionBadge>{t('tenant_home.contact_section.badge')}</SectionBadge>
                            <SectionTitle>
                                {t('tenant_home.contact_section.title')}
                            </SectionTitle>
                            <SectionSubtitle>
                                {t('tenant_home.contact_section.subtitle')}
                            </SectionSubtitle>
                            <CTAButtonWrapper>
                                <CTAButton
                                    variant="primary"
                                    size="M"
                                    icon="chat"
                                    label={t('buttons.contact_rental_concierge')}
                                    onClick={onClickContactRentalConcierge}
                                />
                                <CTAButton
                                    variant="tertiary"
                                    size="M"
                                    label={t('tenant_home.contact_section.browse_homes')}
                                    onClick={() => navigate("/tenant-home#available_units")}
                                />
                            </CTAButtonWrapper>
                        </div>
                    </ExploreSection>
                </Col>
            </Row >

            <Footer />

            {/* Structured Data */}
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": t('tenant_home.how_it_works.title'),
                "description": t('tenant_home.how_it_works.subtitle'),
                "step": howItWorksSteps.map((step, index) => ({
                    "@type": "HowToStep",
                    "position": index + 1,
                    "name": step.title,
                    "text": step.description
                }))
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": t('tenant_home.available_units.title'),
                "itemListElement": UNIT_CARDS.map((unit, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "item": {
                        "@type": "Apartment",
                        "name": unit.title,
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": unit.location,
                            "addressRegion": "Dubai",
                            "addressCountry": "AE"
                        }
                    }
                }))
            }} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.getmanagd.com/home"
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Tenant Home",
                        "item": "https://www.getmanagd.com/tenant-home"
                    }
                ]
            }} />
        </>
    );
};

export default TenantHomePage;
